// /var/www/afra/web-design.rampagesoft.com/my-app/src/component/ContentDetail.js
import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchPostImage, fetchPosts, fetchPostById, incrementVisitors } from '../services/api';
import { stripHtml, truncateText, createUrlFriendlyString, formatDate, getImageUrl } from '../utility/utils';
import MetaTagContentDetail from '../metatag/MetaTagContentDetail';
import Slidebar from '../component/slidebar.js';
import '../css/ContentDetail.scss';
import 'primeicons/primeicons.css';

export default function ContentDetail() {
  const params = useParams();
  const idFromParams = params.id;
  const location = useLocation();

  const [state, setState] = useState({
    content: {
      Home_Sub: 'กำลังโหลดชื่อเรื่อง...',
      Home_Short: '',
      Home_Detail: 'กำลังโหลดเนื้อหา...',
      Home_Date: new Date().toISOString(),
      Home_Visitors: 0,
      Home_ID: null,
    },
    imageUrl: 'https://afaa.website/s/ff6970.png',
    slidebarData: [],
    loading: true,
    error: null,
  });

  const { content, imageUrl, slidebarData, loading, error } = state;
  const heading = 'CONTENT IDEAS LAB';
  const defaultImage = 'https://afaa.website/s/ff6970.png';

  const fetchContentDetail = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const parsedId = parseInt(idFromParams, 10);

      if (!idFromParams || idFromParams.trim() === '' || idFromParams === 'undefined' || idFromParams === 'null') {
        console.error(`[ContentDetail] Invalid or missing ID: '${idFromParams}'`);
        throw new Error('Invalid content ID provided in URL.');
      }

      if (isNaN(parsedId) || parsedId <= 0) {
        console.error(`[ContentDetail] Parsed ID is invalid: ${parsedId} (from string '${idFromParams}')`);
        throw new Error(`Content ID '${idFromParams}' is not a valid number.`);
      }

      //console.log(`[ContentDetail] Fetching post with ID: ${parsedId}`);
      let post = null;
      let data = await fetchPostById(parsedId);
      //console.log(`[ContentDetail] API Response for fetchPostById(${parsedId}):`, data);
      //console.log(`[ContentDetail] Home_IDs in response:`, data.results?.map(p => p.Home_ID));

      post = data.results?.find(p => p.Home_ID === parsedId);
      if (!post && data.total_count > 0) {
        //console.warn(`[ContentDetail] Post ID ${parsedId} not found in initial response. Attempting pagination.`);
        let offset = 10;
        const limit = 10;
        while (offset < data.total_count) {
          //console.log(`[ContentDetail] Fetching page with offset ${offset} for ID ${parsedId}`);
          data = await fetchPosts(limit, offset);
          post = data.results?.find(p => p.Home_ID === parsedId);
          if (post) break;
          offset += limit;
        }
      }

      if (!post) {
        console.error(`[ContentDetail] No post found for ID: ${parsedId}. Final response:`, data);
        throw new Error(`No post found for ID: ${parsedId}`);
      }
      //console.log(`[ContentDetail] Post found for ID ${parsedId}:`, post);

      const finalPost = {
        ...post,
        Home_Sub: post.Home_Sub || 'Untitled Article',
        Home_ID: parsedId,
      };
      if (!post.Home_Sub) {
        console.warn(`[ContentDetail] Home_Sub missing for post ID ${parsedId}. Using default.`);
      }

      let rawImg = '';
      try {
        //console.log(`[ContentDetail] Fetching image for ID: ${parsedId}`);
        const imageData = await fetchPostImage(parsedId);
        rawImg = imageData.results?.[0]?.ImgHome_Img || '';
        //console.log(`[ContentDetail] Raw image data for ID ${parsedId}:`, rawImg);
      } catch (e) {
        console.warn(`[ContentDetail] Failed to fetch image for ID ${parsedId}: ${e.message}`);
      }
      const finalUrl = getImageUrl(rawImg, defaultImage);
      //console.log(`[ContentDetail] Final image URL for ID ${parsedId}:`, finalUrl);

      try {
        //console.log(`[ContentDetail] Incrementing visitors for ID: ${parsedId}`);
        await incrementVisitors(parsedId);
      } catch (e) {
        console.warn(`[ContentDetail] Failed to increment visitors for ID ${parsedId}: ${e.message}`);
      }

      setState(prev => ({
        ...prev,
        content: finalPost,
        imageUrl: finalUrl,
        loading: false,
      }));
    } catch (e) {
      console.error('[ContentDetail] Error during fetchContentDetail:', e.message, e);
      const errorMsg = e.message.includes('Failed to fetch')
        ? 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่อหรือลองใหม่ภายหลัง'
        : `ไม่สามารถโหลดเนื้อหาได้: ${e.message}`;
      setState(prev => ({
        ...prev,
        error: errorMsg,
        content: {
          Home_Sub: 'เกิดข้อผิดพลาด',
          Home_Detail: errorMsg,
          Home_Short: '',
          Home_Date: new Date().toISOString(),
          Home_Visitors: 0,
          Home_ID: null,
        },
        loading: false,
      }));
    }
  }, [idFromParams, defaultImage]);

  const fetchSlidebarData = useCallback(async () => {
    try {
      //console.log('[ContentDetail] Fetching slidebar data...');
      const parsedId = parseInt(idFromParams, 10);
      const resp = await fetchPosts(10);
      const items = await Promise.all(
        (resp.results || [])
          .filter(p => p.Home_ID && p.Home_ID !== parsedId && p.Home_Sub)
          .slice(0, 5)
          .map(async p => {
            let rawSlidebarImg = '';
            try {
              const imgData = await fetchPostImage(p.Home_ID);
              rawSlidebarImg = imgData.results?.[0]?.ImgHome_Img || '';
            } catch (e) {
              console.warn(`[ContentDetail] Failed to fetch slidebar image for Home_ID ${p.Home_ID}: ${e.message}`);
            }
            const url = getImageUrl(rawSlidebarImg, defaultImage);
            const title = truncateText(stripHtml(p.Home_Sub || ''), 50);
            const desc = truncateText(stripHtml(p.Home_Short || ''), 200);
            const link = p.Home_ID
              ? `/content/${p.Home_ID}/${createUrlFriendlyString(stripHtml(p.Home_Sub || 'untitled'))}`
              : '#';
            return { id: p.Home_ID, img: url, title, description: desc, link, namelink: 'Read More', style: {} };
          })
      );
      setState(prev => ({ ...prev, slidebarData: items }));
      //console.log('[ContentDetail] Slidebar data fetched:', items);
    } catch (e) {
      console.warn('[ContentDetail] Failed to fetch slidebar data:', e.message);
    }
  }, [idFromParams, defaultImage]);

  useLayoutEffect(() => {
    if (idFromParams) {
      // Scroll to top when a new ContentDetail is opened
      window.scrollTo(0, 0);
      setState(prev => ({
        ...prev,
        content: {
          Home_Sub: 'กำลังโหลดชื่อเรื่องใหม่...',
          Home_Short: '',
          Home_Detail: 'กำลังโหลดเนื้อหาใหม่...',
          Home_Date: new Date().toISOString(),
          Home_Visitors: 0,
          Home_ID: null,
        },
        loading: true,
        error: null,
      }));
    }
  }, [idFromParams]);

  useEffect(() => {
    //console.log('[ContentDetail] useEffect triggered. Location:', JSON.stringify(location));

    JSON.stringify(location);
    //console.log('[ContentDetail] useParams:', JSON.stringify(params));
    //console.log(`[ContentDetail] ID: '${idFromParams}' (type: ${typeof idFromParams})`);

    if (idFromParams) {
      fetchContentDetail();
      fetchSlidebarData();
    } else {
      setState(prev => ({
        ...prev,
        error: 'ID ของเนื้อหาไม่ถูกต้องหรือไม่พบใน URL',
        content: {
          Home_Sub: 'เกิดข้อผิดพลาด',
          Home_Detail: 'ID ของเนื้อหาไม่ถูกต้องหรือไม่พบใน URL',
          Home_Short: '',
          Home_Date: new Date().toISOString(),
          Home_Visitors: 0,
          Home_ID: null,
        },
        loading: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromParams, fetchContentDetail, fetchSlidebarData]);

  const breadcrumbTitle = useMemo(
    () =>
      error
        ? 'Error'
        : content.Home_Sub === 'กำลังโหลดชื่อเรื่อง...' ||
          content.Home_Sub === 'กำลังโหลดชื่อเรื่องใหม่...' ||
          content.Home_Sub === 'เกิดข้อผิดพลาด'
        ? 'Loading...'
        : truncateText(stripHtml(content.Home_Sub || 'Untitled'), 50),
    [error, content.Home_Sub]
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Home', url: '/', icon: 'pi pi-home' },
      { label: 'Content', url: '/content', icon: 'pi pi-folder' },
      { label: breadcrumbTitle, disabled: true },
    ],
    [breadcrumbTitle]
  );

  const pageUrlFriendlySlug = useMemo(
    () =>
      content.Home_Sub &&
      content.Home_Sub !== 'กำลังโหลดชื่อเรื่อง...' &&
      content.Home_Sub !== 'กำลังโหลดชื่อเรื่องใหม่...' &&
      content.Home_Sub !== 'เกิดข้อผิดพลาด' &&
      !error
        ? createUrlFriendlyString(stripHtml(content.Home_Sub))
        : 'details',
    [content.Home_Sub, error]
  );

  const currentUrl = useMemo(
    () => `${window.location.origin}/content/${idFromParams || 'invalid-id'}/${pageUrlFriendlySlug}`,
    [idFromParams, pageUrlFriendlySlug]
  );

  return (
    <section>
      <MetaTagContentDetail content={content} currentUrl={currentUrl} imageUrl={imageUrl} />
      <div className="container">
        <BreadCrumb model={breadcrumbItems} className="content-detail__breadcrumb" />
        {loading ? (
          <div className="content-detail__loading">
            <ProgressSpinner />
            <p>กำลังโหลดรายละเอียดบทความ...</p>
          </div>
        ) : error ? (
          <div className="content-detail__error">
            <h2>เกิดข้อผิดพลาด</h2>
            <p>{error}</p>
            <p>ID ที่พยายามเข้าถึง: '{idFromParams || 'ไม่มี'}'</p>
          </div>
        ) : (
          <article className="content-detail">
            <header className="content-detail__header">
              <h1 className="content-detail__title">{stripHtml(content.Home_Sub)}</h1>
              <div className="content-detail__meta">
                <span className="content-detail__meta-item">
                  <i className="pi pi-calendar" /> {formatDate(content.Home_Date || new Date())}
                </span>
                <span className="content-detail__meta-item">
                  <i className="pi pi-eye" /> {content.Home_Visitors || 0}
                </span>
              </div>
            </header>
            {imageUrl && (
              <figure className="content-detail__image-container">
                <img
                  src={imageUrl}
                  alt={stripHtml(content.Home_Sub) || 'Content Image'}
                  className="content-detail__image"
                  onError={e => {
                    console.warn(`[ContentDetail] Failed to load image: ${imageUrl}`);
                    e.target.src = defaultImage;
                  }}
                  loading="lazy"
                />
              </figure>
            )}
            <section className="content-detail__details">
              <div
                dangerouslySetInnerHTML={{
                  __html: content.Home_Detail || '<p>No content available.</p>',
                }}
              />
            </section>
          </article>
        )}
      </div>
      <Slidebar data={slidebarData} heading={heading} theme="primary-home" />
    </section>
  );
}